/*
 * Copyright (C) 2025 Hendrik. All Rights Reserved.
 */

import React, { useState, useEffect } from 'react';

import axios from 'axios';
import './dashboard.css'; // Reusing your dashboard styles for consistency

// --- SVG Icons ---
const Icons = {
    Edit: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>,
    Delete: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path></svg>,
    Report: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
};

export default function AllStudents() {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [gradeFilter, setGradeFilter] = useState('all');
    const [unenrolledFilter, setUnenrolledFilter] = useState('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedStudents, setSelectedStudents] = useState([]);

    useEffect(() => {
        loadStudents();
    }, []);

    const loadStudents = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/fetch_all_students.php`);
            const students = response.data.students || [];
            // Sort by Last Name, then First Name
            students.sort((a, b) => {
                const lastA = (a.last_name || '').toLowerCase();
                const lastB = (b.last_name || '').toLowerCase();
                if (lastA < lastB) return -1;
                if (lastA > lastB) return 1;

                const firstA = (a.first_name || '').toLowerCase();
                const firstB = (b.first_name || '').toLowerCase();
                if (firstA < firstB) return -1;
                if (firstA > firstB) return 1;
                return 0;
            });
            setStudents(students);
            setLoading(false);
        } catch (error) {
            setStudents([]);
            setLoading(false);
        }
    };

    // Filter Logic
    const uniqueGrades = [...new Set(students.map(s => parseInt(s.grade_level)).filter(g => g > 0))].sort((a, b) => a - b);

    const searchedStudents = searchQuery.trim() === ''
    ? students
    : students.filter(student => 
        `${student.first_name} ${student.last_name}`.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredStudents = gradeFilter === 'all'
        ? searchedStudents
        : searchedStudents.filter(student => parseInt(student.grade_level) === parseInt(gradeFilter));


const finalFilteredStudents = unenrolledFilter === 'unenrolled'
    ? filteredStudents.filter(student => parseInt(student.subject_count) === 0)
    : filteredStudents;
    // Handlers

    const handleSelectStudent = (studentId) => {
        setSelectedStudents(prev => 
            prev.includes(studentId) 
                ? prev.filter(id => id !== studentId) 
                : [...prev, studentId]
        );
    };

    const handleSelectAll = (e) => {
        if (e.target.checked) {
            // Select all CURRENTLY VISIBLE students
            const allVisibleIds = finalFilteredStudents.map(s => s.student_id);
            setSelectedStudents(allVisibleIds);
        } else {
            // Uncheck all
            setSelectedStudents([]);
        }
    };

    const handleBulkDelete = async () => {
        if (!window.confirm(`Are you sure you want to permanently delete ${selectedStudents.length} selected students?`)) return;

        try {
            // Run the individual delete script for every selected ID simultaneously
            await Promise.all(selectedStudents.map(id => 
                axios.post(`${process.env.REACT_APP_API_URL}/delete_student_completely.php`, { student_id: id })
            ));
            
            // Remove them from the local state so the UI updates
            setStudents(students.filter(s => !selectedStudents.includes(s.student_id)));
            setSelectedStudents([]); // Clear selection
            alert('Selected students deleted successfully.');
        } catch (error) {
            alert('Failed to delete some or all selected students.');
        }
    };



    const handleDeleteStudent = async (studentId, studentName) => {
        if (!window.confirm(`Permanently delete ${studentName}? This removes ALL their marks and data.`)) return;

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/delete_student_completely.php`, { student_id: studentId });
            if (res.data.success) {
                setStudents(students.filter(s => s.student_id !== studentId));
            } else {
                alert('Error: ' + (res.data.error || 'Unknown error'));
            }
        } catch (error) {
            alert('Failed to delete student.');
        }
    };

    const handleEditStudent = async (student) => {
        const newFirstName = prompt('First Name:', student.first_name);
        if (newFirstName === null) return;

        const newLastName = prompt('Last Name:', student.last_name);
        if (newLastName === null) return;

        const newGrade = prompt('Grade (1-12):', student.grade_level);
        if (newGrade === null) return;

        const gradeNum = parseInt(newGrade);
        if (isNaN(gradeNum) || gradeNum < 1 || gradeNum > 12) return alert('Invalid grade');

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/update_student.php`, {
                student_id: student.student_id,
                first_name: newFirstName.trim(),
                last_name: newLastName.trim(),
                grade_level: gradeNum
            });

            if (res.data.success) {
                setStudents(students.map(s =>
                    s.student_id === student.student_id
                        ? { ...s, first_name: newFirstName.trim(), last_name: newLastName.trim(), grade_level: gradeNum }
                        : s
                ));
            } else {
                alert('Update failed');
            }
        } catch (error) {
            alert('Update failed');
        }
    };

    const handleDeleteAllStudents = async () => {
        const confirm1 = window.confirm('WARNING: You are about to delete ALL students and ALL marks from the ENTIRE database.');
        if (!confirm1) return;

        const confirm2 = window.confirm('Are you absolutely sure? This cannot be undone.');
        if (!confirm2) return;

        try {
            const res = await axios.post(`${process.env.REACT_APP_API_URL}/delete_all_students_completely.php`);
            const data = typeof res.data === 'string' ? JSON.parse(res.data) : res.data;

            if (data && typeof data.students_deleted === 'number') {
                alert(`Database cleared. Deleted ${data.students_deleted} students.`);
                setStudents([]);
            } else {
                alert('Error deleting database.');
            }
        } catch (error) {
            alert('Failed to delete database.');
        }
    };

    return (
        <div className="students-page-wrapper">
            <style>{`
                /* Modern CSS for AllStudents Component */
                .students-page-wrapper {
                    margin-left: 220px; /* Aligns with Sidebar */
                    padding: calc(40px + var(--shell-top-offset)) 40px 40px;
                    background-color: #f6f8fa;
                    min-height: 100vh;
                    font-family: 'Inter', sans-serif;
                }

                .search-input {
                    padding: 10px 16px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    min-width: 200px;
                    background-color: #fff;
                    margin-top: 8px;
                }

                .page-header {
                    background: white;
                    padding: 24px 32px;
                    border-radius: 12px;
                    box-shadow: 0 4px 6px -1px rgba(0,0,0,0.05);
                    margin-bottom: 24px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    border: 1px solid #f0f0f0;
                }

                .header-title h1 {
                    margin: 0 0 8px 0;
                    font-size: 1.8rem;
                    color: #2c3e50;
                }

                .header-title p {
                    margin: 0;
                    color: #7f8c8d;
                    font-size: 0.95rem;
                }

                /* Filter Section */
                .filter-bar {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                }

                .modern-select {
                    padding: 10px 16px;
                    border: 1px solid #e0e0e0;
                    border-radius: 8px;
                    font-size: 0.9rem;
                    min-width: 150px;
                    background-color: #f8f9fa;
                    cursor: pointer;
                }

                .count-badge {
                    background: #e8f5e9;
                    color: #2e7d32;
                    padding: 6px 12px;
                    border-radius: 20px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }

                /* Danger Zone */
                .danger-zone {
                    background: #fff5f5;
                    border: 1px solid #fed7d7;
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 30px;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .danger-content h3 {
                    margin: 0 0 4px 0;
                    color: #c53030;
                    font-size: 1.1rem;
                }

                .danger-content p {
                    margin: 0;
                    color: #e53e3e;
                    font-size: 0.9rem;
                }

                .btn-danger-zone {
                    background: #c53030;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: background 0.2s;
                }

                .btn-danger-zone:hover {
                    background: #9b2c2c;
                }

                /* Modern Table */
                .table-card {
                    background: white;
                    border-radius: 12px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.05);
                    border: 1px solid #f0f0f0;
                    overflow: hidden;
                }

                .table-responsive {
                    overflow-x: auto;
                }

                .students-table {
                    width: 100%;
                    border-collapse: collapse;
                    min-width: 900px;
                }

                .students-table th {
                    background: #f8f9fa;
                    padding: 16px 24px;
                    text-align: left;
                    font-weight: 600;
                    color: #7f8c8d;
                    text-transform: uppercase;
                    font-size: 0.8rem;
                    border-bottom: 2px solid #eee;
                }

                .students-table td {
                    padding: 16px 24px;
                    border-bottom: 1px solid #eee;
                    color: #2c3e50;
                    font-size: 0.95rem;
                }

                .students-table tr:hover {
                    background-color: #fafafa;
                }

                .student-name {
                    font-weight: 600;
                }

                .grade-pill {
                    background: #e3f2fd;
                    color: #1565c0;
                    padding: 4px 10px;
                    border-radius: 12px;
                    font-size: 0.8rem;
                    font-weight: 600;
                }

                .subject-list {
                    color: #666;
                    font-size: 0.85rem;
                    line-height: 1.4;
                }

                .actions-cell {
                    display: flex;
                    gap: 8px;
                    justify-content: flex-end;
                }

                .icon-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 6px;
                    border: 1px solid #eee;
                    background: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    color: #666;
                    transition: all 0.2s;
                }

                .icon-btn.edit:hover { background: #e3f2fd; color: #1976d2; border-color: #1976d2; }
                .icon-btn.delete:hover { background: #ffebee; color: #d32f2f; border-color: #d32f2f; }
                .icon-btn.report:hover { background: #e8f5e9; color: #2e7d32; border-color: #2e7d32; }

                /* Responsive */
                @media (max-width: 900px) {
                    .students-page-wrapper { margin-left: 0; padding: var(--shell-top-offset-mobile) 20px 20px; }
                    .page-header { flex-direction: column; align-items: flex-start; gap: 15px; }
                    .danger-zone { flex-direction: column; gap: 15px; text-align: center; }
                }
            `}</style>

            {/* Header */}
            <div className="page-header">
                <div className="header-title">
                    <h1>All Students</h1>
                    {/* Fixed: Use students.length directly */}
                    <p>Directory of all enrolled learners ({students.length} Total)</p>
                    <input className="search-input" type="text" placeholder="Search by name..." value={searchQuery} onChange={(e) => {
                        const searchTerm = e.target.value.toLowerCase();
                        setSearchQuery(e.target.value);
                     
                    }} />
                </div>

                {selectedStudents.length > 0 && (
        <div style={{ marginTop: '16px', padding: '12px', background: '#ffebee', borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', border: '1px solid #ffcdd2' }}>
            <span style={{ color: '#c62828', fontWeight: '600' }}>
                {selectedStudents.length} student(s) selected
            </span>
            <button 
                onClick={handleBulkDelete}
                style={{ background: '#d32f2f', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold' }}
            >
                Delete Selected
            </button>
        </div>
    )}

                <div className="filter-bar">
                    <select
                        className="modern-select"
                        value={gradeFilter}
                        onChange={(e) => setGradeFilter(e.target.value)}
                    >
                        <option value="all">All Grades</option>
                        {uniqueGrades.map(grade => (
                            <option key={grade} value={grade}>Grade {grade}</option>
                        ))}
                    </select>
                      {/*Select to filter by unenrolled status Not enrolled*/}
                    <select
                        className="modern-select"
                        value={unenrolledFilter}
                        onChange={(e) => setUnenrolledFilter(e.target.value)}
                    >
                        <option value="all">All Students</option>
                        <option value="unenrolled">Unenrolled Only</option>
                    </select>
                    <span className="count-badge">
                        {finalFilteredStudents.length} Visible
                    </span>

                  
                </div>
            </div>

            {/* Danger Zone */}
            <div className="danger-zone">
                <div className="danger-content">
                    <h3>⚠️ Danger Zone</h3>
                    <p>Actions here are irreversible. Be careful.</p>
                </div>
                <button className="btn-danger-zone" onClick={handleDeleteAllStudents}>
                    Delete All Students
                </button>
            </div>

            {/* Data Table */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>Loading student directory...</div>
            ) : (
                <div className="table-card">
                    <div className="table-responsive">
                        <table className="students-table">
                           <thead>
    <tr>
        {/* New Header Checkbox */}
        <th style={{ width: '40px', textAlign: 'center' }}>
            <input 
                type="checkbox" 
                style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                onChange={handleSelectAll}
                checked={finalFilteredStudents.length > 0 && selectedStudents.length === finalFilteredStudents.length}
            />
        </th>
        <th>Student Name</th>
        <th>Grade Level</th>
        <th>Enrolled Subjects</th>
        <th style={{ textAlign: 'center' }}>Count</th>
        <th style={{ textAlign: 'right' }}>Actions</th>
    </tr>
</thead>
                            <tbody>
                                {finalFilteredStudents.length > 0 ? (
                                    finalFilteredStudents.map((student) => (
                                        <tr key={student.student_id}>
                                            <td style={{ textAlign: 'center' }}>
                    <input 
                        type="checkbox" 
                        style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                        checked={selectedStudents.includes(student.student_id)}
                        onChange={() => handleSelectStudent(student.student_id)}
                    />
                </td>
                                            <td className="student-name">
                                                {student.last_name}, {student.first_name}
                                            </td>
                                            <td>
                                                <span className="grade-pill">
                                                    Grade {parseInt(student.grade_level) || '?'}
                                                </span>
                                            </td>
                                            <td className="subject-list">
                                                {student.subjects || <span style={{ color: '#ccc', fontStyle: 'italic' }}>No subjects enrolled</span>}
                                            </td>
                                            <td style={{ textAlign: 'center', fontWeight: 'bold' }}>
                                                {student.subject_count}
                                            </td>
                                            <td>
                                                <div className="actions-cell">
                                                    <button className="icon-btn edit" onClick={() => handleEditStudent(student)} title="Edit Details">
                                                        <Icons.Edit />
                                                    </button>

                                                    <button className="icon-btn delete" onClick={() => handleDeleteStudent(student.student_id, `${student.first_name} ${student.last_name}`)} title="Delete Student">
                                                        <Icons.Delete />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                                            No students found matching your filter.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}


        </div>
    );
}
